import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle, AlertTriangle, CheckCircle2, MessageCircle, MessageSquare,
} from 'lucide-react';
import { getCourseThreads, getThread, replyToThread } from '../../api/threads';
import { unwrapList } from '../../utils/api';
import { getTeacherCourses } from '../../api/teacher';
import { validateAndSanitizeInput } from '../../utils/sanitize';
import { usePolling } from '../../hooks/usePolling';
import SubTabs from '../../components/SubTabs/SubTabs';
import Avatar from '../../components/Avatar/Avatar';
import PageHead from '../../components/PageHead/PageHead';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import { useHideBottomNav, useMediaQuery } from '../../components/Layout/shellState';
import CourseSelect from './CourseSelect';
import useTeacherCourse from './useTeacherCourse';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from './pluralKey';
import { errorText } from '../../utils/errorText';
import { failure, failureText } from './apiError';
import { formatDate as formatCalendarDate } from '../../utils/datetime';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import './TeacherQA.css';

const POLL_MS = 15000;

/**
 * A thread has no title of its own (LessonThread stores the question text only), so the lesson
 * it was asked in heads it; the start of the question stands in when that is unknown.
 */
function threadHeading(thread) {
  if (thread.title) return thread.title;
  if (thread.lessonTitle) return thread.lessonTitle;
  const text = (thread.body || thread.content || '').replace(/\s+/g, ' ').trim();
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

const isAnsweredThread = (thread) => Boolean(thread.isAnswered || thread.status === 'ANSWERED');

/**
 * Urgency of an unanswered thread by its age: >72h => 'urgency-red', >48h => 'urgency-amber',
 * else none. A thread without a date counts as new (no urgency).
 */
const getUrgencyClass = (thread) => {
  if (isAnsweredThread(thread)) return '';
  const rawDate = thread.askedAt || thread.createdAt;
  if (!rawDate) return '';
  const ageHours = (Date.now() - new Date(rawDate).getTime()) / (1000 * 60 * 60);
  if (ageHours > 72) return 'urgency-red';
  if (ageHours > 48) return 'urgency-amber';
  return '';
};

/**
 * TeacherQA — Teacher Q&A Console
 *
 * Shows all Q&A threads for a teacher's courses, grouped by lesson.
 * Unresolved threads older than 48h are highlighted amber/red.
 * Teacher can open a thread and post a reply.
 */
function TeacherQA() {
  const { courseId: routeCourseId } = useParams();
  const { t, lang } = useI18n();

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, selectCourse] = useTeacherCourse(courses, { preferred: routeCourseId || '' });
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replySaving, setReplySaving] = useState(false);
  const [error, setError] = useState(null);
  const [notAssigned, setNotAssigned] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('unanswered');
  // An open thread is a focused screen: on a phone the tab bar steps aside and the reply box is
  // pinned to the bottom edge instead of sitting under every reply.
  const phone = useMediaQuery('(max-width: 639px)', false);
  useHideBottomNav(Boolean(selectedThread));
  // Ref to hold current selectedThread so the polling callback never captures a stale value
  const selectedThreadRef = useRef(selectedThread);
  useEffect(() => { selectedThreadRef.current = selectedThread; }, [selectedThread]);

  const fetchCourses = useCallback(async () => {
    try {
      const result = await getTeacherCourses();
      const list = Array.isArray(result) ? result : result.data || [];
      setCourses(list);
      if (list.length === 0) {
        // Nothing is assigned: no thread request will run, so say so instead of showing a skeleton forever
        setNotAssigned(true);
        setLoading(false);
      }
    } catch (err) {
      setError(failure(err, t('teacher.qa.failedToLoadCourses')));
      // No course means no thread request will ever run: stop the skeleton, show the error card
      setLoading(false);
    }
  }, [t]);

  // Fetch teacher's courses on mount
  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  /**
   * Fetch every thread of a course in one request (GET /courses/:courseId/threads). Each thread
   * carries its lessonId (needed to open and reply later) and its lessonTitle (its heading). All
   * statuses come back: the three tabs count them.
   */
  const fetchAllCourseThreads = async (cid, { silent = false } = {}) => {
    // The 15 s refresh runs quietly: the list stays on screen (and where it was scrolled).
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      setThreads(unwrapList(await getCourseThreads(cid)));
      setError(null);
      setNotAssigned(false);
    } catch (err) {
      if (silent) return; // a failed quiet refresh leaves the list as it was
      setThreads([]);
      if (err?.code === 403) {
        setNotAssigned(true);
        setError(null);
      } else {
        setNotAssigned(false);
        setError(failure(err, t('teacher.qa.failedToLoadThreads')));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch threads when course changes
  useEffect(() => {
    if (selectedCourseId) {
      fetchAllCourseThreads(selectedCourseId);
    }
    // fetchAllCourseThreads is a stable closure over setters + t; re-run on course change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  // Refresh every 15 s while the list is on screen and the tab is visible (see hooks/usePolling).
  usePolling(() => {
    if (!selectedThreadRef.current && selectedCourseId) {
      fetchAllCourseThreads(selectedCourseId, { silent: true });
    }
  }, POLL_MS, Boolean(selectedCourseId));

  const handleCourseChange = (courseId) => {
    if (courseId === selectedCourseId) return;
    selectCourse(courseId);
    setThreads([]);
    setLoading(true);
  };

  const openThread = async (thread) => {
    setSelectedThread(thread);
    setDetailLoading(true);
    setReplyError(null);
    try {
      const lessonId = thread.lessonId || thread.lesson?.id || 'unknown';
      const data = await getThread(selectedCourseId, lessonId, thread.id);
      setThreadDetail(data);
    } catch {
      setThreadDetail(thread);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeThread = () => {
    setSelectedThread(null);
    setThreadDetail(null);
    setReplyContent('');
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!selectedThread) return;

    const validation = validateAndSanitizeInput(replyContent, { minLength: 1, maxLength: 2000 });
    if (!validation.valid) {
      // validation.error is English text from utils/sanitize: say it in the page's own words
      setReplyError(t('teacher.qa.failedToPostReply'));
      return;
    }

    setReplySaving(true);
    setReplyError(null);
    try {
      const lessonId = selectedThread.lessonId || selectedThread.lesson?.id || 'unknown';
      // Backend CreateReplyDto expects { body }, not { content }.
      await replyToThread(selectedCourseId, lessonId, selectedThread.id, {
        body: validation.sanitized,
      });
      setReplyContent('');
      // H-1: wrap refresh in try/catch — reply was already posted, stale detail is non-fatal
      try {
        const updated = await getThread(selectedCourseId, lessonId, selectedThread.id);
        setThreadDetail(updated);
      } catch {
        setReplyError(t('teacher.qa.replyPostedRefresh'));
      }
      // Also update thread status in list
      setThreads((prev) =>
        prev.map((th) =>
          th.id === selectedThread.id ? { ...th, isAnswered: true, status: 'ANSWERED' } : th
        )
      );
    } catch (err) {
      setReplyError(errorText(err, t('teacher.qa.failedToPostReply')));
    } finally {
      setReplySaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return t('teacher.qa.lessHourAgo');
    if (diffHours < 24) return t('teacher.qa.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('teacher.qa.daysAgo', { count: diffDays });
    return formatCalendarDate(date, lang);
  };

  const askedAt = (thread) => new Date(thread.askedAt || thread.createdAt).getTime() || 0;
  const filteredThreads = threads.filter((th) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'unanswered') return !isAnsweredThread(th);
    if (filterStatus === 'answered') return isAnsweredThread(th);
    return true;
  }).sort((a, b) => {
    // Unanswered first. Among them the one that has waited longest comes first (that is where
    // the amber and red ones are); answered threads read newest first.
    const aAnswered = isAnsweredThread(a);
    const bAnswered = isAnsweredThread(b);
    if (!aAnswered && bAnswered) return -1;
    if (aAnswered && !bAnswered) return 1;
    return aAnswered ? askedAt(b) - askedAt(a) : askedAt(a) - askedAt(b);
  });

  const course = courses.find((c) => c.id === selectedCourseId);

  // Thread detail view
  if (selectedThread) {
    const replies = threadDetail?.replies || [];
    const answered = isAnsweredThread(threadDetail || {});
    const asker = selectedThread.authorName || t('teacher.qa.studentFallback');
    return (
      <div className="teacher-qa">
        <PageHead
          back={{ onClick: closeThread, label: t('teacher.qa.backToQA') }}
          eyebrow={course ? (course.title || course.name) : undefined}
          title={t('teacher.qa.title')}
          actions={answered ? <span className="ds-badge ds-badge-success">{t('teacher.qa.answered')}</span> : null}
        />

        <section className="section-card question-card">
          <h2 className="tqa-question-title">{threadHeading(selectedThread)}</h2>
          <p className="question-body">{selectedThread.body || selectedThread.content}</p>
          <div className="question-meta tqa-asker">
            <Avatar name={asker} id={selectedThread.authorId || selectedThread.id} size="sm" />
            <span className="tqa-asker__name">{asker}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(selectedThread.askedAt || selectedThread.createdAt)}</span>
          </div>
        </section>

        <section className="section-card replies-section">
          <div className="section-head">
            <h2 className="section-head__title">{t('teacher.qa.replies')} ({replies.length})</h2>
          </div>
          {detailLoading ? (
            <LoadingState message={t('teacher.qa.loadingReplies')} compact />
          ) : replies.length === 0 ? (
            <InlineEmpty kind="messages" title={t('teacher.qa.noRepliesYet')} />
          ) : (
            <ul className="tqa-replies">
              {replies.map((reply) => {
                const fromTeacher = Boolean(reply.isTeacherReply || reply.isTeacherResponse);
                return (
                  <li key={reply.id} className={`reply-card tqa-reply${fromTeacher ? ' teacher-reply is-teacher' : ''}`}>
                    <Avatar
                      name={reply.authorName || t('teacher.qa.userFallback')}
                      id={reply.authorId || reply.id}
                      size="sm"
                    />
                    <div className="tqa-reply__bubble">
                      <div className="reply-header">
                        <strong>{reply.authorName || t('teacher.qa.userFallback')}</strong>
                        {fromTeacher && (
                          <span className="ds-badge ds-badge-info">{t('teacher.qa.teacherBadge')}</span>
                        )}
                        <span className="reply-time">{formatDate(reply.createdAt)}</span>
                      </div>
                      <p>{reply.body || reply.content}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <form
          className={`reply-form ${phone ? 'ui-sticky-actions tqa-composer' : 'section-card'}`}
          onSubmit={handleReply}
        >
          <div className="ui-field tqa-composer__field">
            <label className={`ui-label${phone ? ' visually-hidden' : ''}`} htmlFor="tqa-reply">{t('teacher.qa.postReply')}</label>
            <textarea
              id="tqa-reply"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={t('teacher.qa.replyPlaceholder')}
              maxLength={2000}
              rows={phone ? 2 : 5}
            />
            {!phone && <p className="ui-hint tqa-char-count">{t('teacher.qa.charCount', { count: replyContent.length })}</p>}
          </div>

          {replyError && (
            <div className="ui-alert tqa-reply-alert" role="alert">
              <AlertCircle aria-hidden="true" />
              <p>{replyError}</p>
            </div>
          )}

          <div className="tqa-actions">
            {/* On a phone the back button above already leaves the thread */}
            {!phone && (
              <button type="button" className="ds-btn ds-btn-secondary" onClick={closeThread}>
                {t('teacher.qa.cancel')}
              </button>
            )}
            <button
              type="submit"
              className={`ds-btn ds-btn-primary${replySaving ? ' is-loading' : ''}`}
              disabled={replySaving || replyContent.trim() === ''}
              aria-busy={replySaving || undefined}
            >
              {t('teacher.qa.postReplyBtn')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const unansweredCount = threads.filter((th) => !isAnsweredThread(th)).length;
  const tabs = [
    { key: 'unanswered', label: t('teacher.qa.filterUnanswered') },
    { key: 'answered', label: t('teacher.qa.filterAnswered') },
    { key: 'all', label: t('teacher.qa.filterAll') },
  ].map((tab) => {
    if (loading || notAssigned) return tab;
    const count = tab.key === 'all' ? threads.length : tab.key === 'unanswered' ? unansweredCount : threads.length - unansweredCount;
    return { ...tab, count };
  });
  // The colour legend only matters while a late thread is on screen
  const hasLate = filteredThreads.some((th) => getUrgencyClass(th));

  const threadList = () => {
    if (loading) return <LoadingState message={t('teacher.qa.loading')} skeleton="list" rows={5} />;
    if (error && threads.length === 0) {
      return (
        <PageEmptyState
          variant="error"
          title={error.title}
          description={error.detail}
          onRetry={() => (selectedCourseId ? fetchAllCourseThreads(selectedCourseId) : fetchCourses())}
        />
      );
    }
    if (filteredThreads.length === 0) {
      return (
        <PageEmptyState
          variant={filterStatus === 'unanswered' ? 'success' : 'inbox'}
          title={filterStatus === 'unanswered' ? t('teacher.qa.noUnanswered') : t('teacher.qa.noThreads')}
          description={filterStatus === 'unanswered' ? t('teacher.qa.noUnansweredDesc') : t('teacher.qa.noThreadsDesc')}
        />
      );
    }
    return (
      <div className="section-card tqa-list ui-list--feed">
        {filteredThreads.map((thread) => {
          const urgency = getUrgencyClass(thread);
          const answered = isAnsweredThread(thread);
          const replyCount = thread.replies?.length ?? thread.replyCount ?? 0;
          const tone = answered ? 'success' : urgency === 'urgency-red' ? 'danger' : urgency === 'urgency-amber' ? 'warning' : 'info';
          const Icon = answered ? CheckCircle2 : urgency ? AlertTriangle : MessageCircle;
          const preview = thread.body || thread.content || '';
          return (
            <div
              key={thread.id}
              role="button"
              tabIndex={0}
              className={`ui-list-row is-interactive thread-item tqa-thread ${answered ? 'answered' : 'unanswered'} ${urgency}`.trim()}
              onClick={() => openThread(thread)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openThread(thread); } }}
            >
              <span className="ui-icon-chip ui-icon-chip--md" data-tone={tone} aria-hidden="true"><Icon /></span>
              <div className="thread-content tqa-thread__body">
                <div className="thread-item-header">
                  <h3 className="thread-title">{threadHeading(thread)}</h3>
                  {urgency === 'urgency-red' && (
                    <span className="urgency-badge ds-badge ds-badge-danger" title={t('teacher.qa.legendRed')}>{t('teacher.qa.urgencyBadge72')}</span>
                  )}
                  {urgency === 'urgency-amber' && (
                    <span className="urgency-badge ds-badge ds-badge-warning" title={t('teacher.qa.legendAmber')}>{t('teacher.qa.urgencyBadge48')}</span>
                  )}
                </div>
                <p className="thread-preview">
                  {preview.substring(0, 120)}
                  {preview.length > 120 ? '...' : ''}
                </p>
                <div className="thread-meta">
                  <span>{thread.authorName || t('teacher.qa.studentFallback')}</span>
                  <span className="separator" aria-hidden="true">·</span>
                  <span>{formatDate(thread.askedAt || thread.createdAt)}</span>
                </div>
              </div>
              <div className="thread-stats">
                <span className="stat-label">
                  {replyCount} {t(`teacher.qa.replies${pluralSuffix(lang, replyCount)}`, { count: replyCount })}
                </span>
                <span className={`ds-badge ${answered ? 'ds-badge-success' : 'ds-badge-info'}`}>
                  {answered ? t('teacher.qa.answered') : t('teacher.qa.open')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="teacher-qa">
      <PageHead
        icon={<MessageSquare />}
        title={t('teacher.qa.title')}
        description={t('teacher.qa.subtitle')}
        actions={!notAssigned && courses.length > 1 ? (
          <CourseSelect
            courses={courses}
            value={selectedCourseId}
            onChange={handleCourseChange}
            ariaLabel={t('teacher.qa.courseLabel')}
            placeholder={t('teacher.qa.selectCoursePlaceholder')}
          />
        ) : null}
      />

      {error && threads.length > 0 && (
        <div className="ui-alert tqa-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{failureText(error)}</p>
        </div>
      )}

      {notAssigned ? (
        <PageEmptyState
          variant="forbidden"
          title={t('teacher.qa.noCourseAccessTitle')}
          description={t('teacher.qa.noCourseAccess')}
        />
      ) : (
        <>
          {/* No tabs over a failed load: their counts would say "0" about a list that never arrived */}
          {!(error && threads.length === 0 && !loading) && (
          <div className="tqa-toolbar">
            <SubTabs
              tabs={tabs}
              active={filterStatus}
              onChange={setFilterStatus}
              ariaLabel={t('teacher.qa.filterAria')}
            />
            <div className="tqa-aside">
              {hasLate && (
                <div className="tqa-legend" role="group" aria-label={t('teacher.qa.legendAria')}>
                  <span className="ds-badge ds-badge-warning ds-badge--dot">{t('teacher.qa.legendAmber')}</span>
                  <span className="ds-badge ds-badge-danger ds-badge--dot">{t('teacher.qa.legendRed')}</span>
                </div>
              )}
              <span className="tqa-live ds-badge ds-badge-success ds-badge--dot" title={t('teacher.qa.updatesEvery15s')}>
                {t('teacher.qa.liveBadge')}
                <span className="visually-hidden">: {t('teacher.qa.updatesEvery15s')}</span>
              </span>
            </div>
          </div>
          )}

          {threadList()}
        </>
      )}
    </div>
  );
}

export default TeacherQA;
